import axios from '../../../api-client.ts';
import clsx from 'clsx';
import {Form, Formik} from 'formik';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {KTCard, KTCardBody, KTSVG, toAbsoluteStatic,} from '../../../../_mesomb/helpers';
import ButtonIndicator from '../../../../_mesomb/partials/components/ButtonIndicator.tsx';
import HModal from '../../../../_mesomb/partials/components/HModal.tsx';
import FormField from '../../../../_mesomb/partials/form/FormField.tsx';
import PublicDisputeHistoryWidget from './PublicDisputeHistoryWidget.tsx';
import {SecureDisputeStatus, SecureTransactionModel, SecureTransactionStatus,} from '../../../../types';
import {formatDateTime, formatPhoneNumber} from "../../../../_mesomb/utils/format.ts";
import {THEMES} from "../../../../_mesomb/partials/components/HStatus.tsx";
import {InfoRow} from "../../../../_mesomb/partials/components/Info.tsx";
import i18n from "../../../../_mesomb/i18n";

// ─── helpers ──────────────────────────────────────────────────────────────────


const disputeBadge: Record<SecureDisputeStatus, string> = {
  no_dispute: 'badge-light-secondary',
  dispute_opened: 'badge-light-warning',
  resolved_release: 'badge-light-success',
  resolved_refund: 'badge-light-success',
  resolved_partial: 'badge-light-success',
  under_review: 'badge-light-danger',
};

function getStatusBadgeClass(status: SecureTransactionStatus) {
  return `badge-light-${THEMES[status]}`;
}

function extractApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      const message = (data as { message: string }).message.trim();
      if (message) return message;
    }
  }

  return fallback;
}

// ─── confirmation schema ───────────────────────────────────────────────────────

const confirmSchema = Yup.object({
  // token: Yup.string().required(),
});

const reportIssueSchema = Yup.object({
  reason: Yup.string().required(i18n.t('Field_is_required')),
  message: Yup.string().required(i18n.t('Field_is_required')).max(1000),
});

const disputeMessageSchema = Yup.object({
  message: Yup.string().required(i18n.t('Field_is_required')).max(1000),
  token: Yup.string().required(),
});

const disputeEvidenceSchema = Yup.object({
  title: Yup.string().required(i18n.t('Field_is_required')).max(100),
  description: Yup.string().max(500),
  external_url: Yup.string().url().nullable(),
  token: Yup.string().required(),
});

// ─── Confirm modal ─────────────────────────────────────────────────────────────

function ConfirmModal({
  show,
  token,
  onClose,
  onSuccess,
}: {
  show: boolean;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  return (
    <HModal
      show={show}
      handleClose={onClose}
      title={t('Confirm transaction', { defaultValue: 'Confirm transaction' })}
      width={500}
    >
      <Formik
        initialValues={{}}
        validationSchema={confirmSchema}
        onSubmit={async (_, { setSubmitting, setStatus }) => {
          setStatus(undefined);
          try {
            await axios.post(`/secure-pay/${token}/confirm/`);
            onSuccess();
          } catch (error) {
            setStatus(
              extractApiErrorMessage(
                error,
                t('Something went wrong! Please try again later.')
              )
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, status }) => (
          <Form>
            <div className='alert alert-primary d-flex align-items-center gap-3 mb-6'>
              {/*<KTIcon iconName='shield-tick' className='fs-2 text-primary' />*/}
              <KTSVG path="media/gen051.svg" className="svg-icon-muted svg-icon-2hx" />
              <span>
                {t('Confirm transaction escrow description', {
                  defaultValue:
                    'By confirming, you acknowledge that the transaction conditions have been met and authorize the release of funds to the seller.',
                })}
              </span>
            </div>
            {status && <div className='alert alert-danger py-3 mb-6'>{status}</div>}
            <div className='d-flex gap-3'>
              <ButtonIndicator
                type='submit'
                label={t('Confirm and release', { defaultValue: 'Confirm and release' })}
                loading={isSubmitting}
                disabled={isSubmitting}
                className='btn-success'
                marginTop={0}
              />
              <button type='button' className='btn btn-light' onClick={onClose}>
                {t('Cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </HModal>
  );
}

// ─── Report Issue modal ────────────────────────────────────────────────────────

const ISSUE_REASONS = [
  'ITEM_NOT_RECEIVED',
  'ITEM_NOT_AS_DESCRIBED',
  'ITEM_DAMAGED',
  'DUPLICATE_CHARGE',
  'UNAUTHORIZED',
  'OTHER',
];

function ReportIssueModal({
  show,
  token,
  onClose,
  onSuccess,
}: {
  show: boolean;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  return (
    <HModal
      show={show}
      handleClose={onClose}
      title={t('Report an issue', { defaultValue: 'Report an issue' })}
      width={600}
    >
      <Formik
        initialValues={{ reason: ISSUE_REASONS[0], message: '' }}
        validationSchema={reportIssueSchema}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setStatus(undefined);
          try {
            await axios.post(`/secure-pay/${token}/report-issue/`, values);
            onSuccess();
          } catch (error) {
            setStatus(
              extractApiErrorMessage(
                error,
                t('Something went wrong! Please try again later.')
              )
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, errors, touched, status }) => (
          <Form>
            <FormField
              type='select'
              name='reason'
              label={t('Issue reason', { defaultValue: 'Issue reason' })}
              error={errors.reason}
              touched={touched.reason}
              options={ISSUE_REASONS.map((r) => ({
                value: r,
                label: t(`dispute_reason.${r}`, { defaultValue: r.replace(/_/g, ' ') }),
              }))}
              required={true}
            />
            <FormField
              type='textarea'
              name='message'
              label={t('Description', { defaultValue: 'Description' })}
              error={errors.message}
              touched={touched.message}
              placeholder={t('Describe the issue in detail…', {
                defaultValue: 'Describe the issue in detail…',
              })}
              required={true}
            />
            {status && <div className='alert alert-danger py-3 mb-6'>{status}</div>}
            <div className='d-flex gap-3'>
              <ButtonIndicator
                type='submit'
                label={t('Submit issue', { defaultValue: 'Submit issue' })}
                loading={isSubmitting}
                disabled={isSubmitting}
                className='btn-danger'
                marginTop={0}
              />
              <button type='button' className='btn btn-light' onClick={onClose}>
                {t('Cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </HModal>
  );
}

// ─── Dispute message modal ─────────────────────────────────────────────────────

function DisputeMessageModal({
  show,
  token,
  onClose,
  onSuccess,
}: {
  show: boolean;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  return (
    <HModal
      show={show}
      handleClose={onClose}
      title={t('Leave a dispute message', { defaultValue: 'Leave a dispute message' })}
      width={600}
    >
      <Formik
        initialValues={{ token, message: '' }}
        validationSchema={disputeMessageSchema}
        onSubmit={async (values, { setSubmitting, resetForm, setStatus }) => {
          setStatus(undefined);
          try {
            await axios.post('/secure-pay/dispute/message', values);
            resetForm();
            onSuccess();
          } catch (error) {
            setStatus(
              extractApiErrorMessage(
                error,
                t('Something went wrong! Please try again later.')
              )
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, errors, touched, status }) => (
          <Form>
            <FormField
              type='textarea'
              name='message'
              label={t('Message', { defaultValue: 'Message' })}
              error={errors.message}
              touched={touched.message}
              placeholder={t('Your message to support the dispute…', {
                defaultValue: 'Your message to support the dispute…',
              })}
            />
            {status && <div className='alert alert-danger py-3 mb-6'>{status}</div>}
            <div className='d-flex gap-3'>
              <ButtonIndicator
                type='submit'
                label={t('Send message', { defaultValue: 'Send message' })}
                loading={isSubmitting}
                disabled={isSubmitting}
              />
              <button type='button' className='btn btn-light' onClick={onClose}>
                {t('Cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </HModal>
  );
}

// ─── Dispute evidence modal ────────────────────────────────────────────────────

function DisputeEvidenceModal({
  show,
  token,
  onClose,
  onSuccess,
}: {
  show: boolean;
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  return (
    <HModal
      show={show}
      handleClose={onClose}
      title={t('Add dispute evidence', { defaultValue: 'Add dispute evidence' })}
      width={600}
    >
      <Formik
        initialValues={{ token, title: '', description: '', external_url: '' }}
        validationSchema={disputeEvidenceSchema}
        onSubmit={async (values, { setSubmitting, resetForm, setStatus }) => {
          setStatus(undefined);
          try {
            await axios.post('/secure-pay/dispute/evidence', {
              ...values,
              external_url: values.external_url || null,
            });
            resetForm();
            onSuccess();
          } catch (error) {
            setStatus(
              extractApiErrorMessage(
                error,
                t('Something went wrong! Please try again later.')
              )
            );
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, errors, touched, status }) => (
          <Form>
            <FormField
              type='text'
              name='title'
              label={t('Evidence title', { defaultValue: 'Evidence title' })}
              error={errors.title}
              touched={touched.title}
              placeholder={t('e.g. Delivery screenshot', {
                defaultValue: 'e.g. Delivery screenshot',
              })}
            />
            <FormField
              type='textarea'
              name='description'
              label={t('Description', { defaultValue: 'Description' })}
              error={errors.description}
              touched={touched.description}
              required={false}
              placeholder={t('Optional details about this evidence…', {
                defaultValue: 'Optional details about this evidence…',
              })}
            />
            <FormField
              type='url'
              name='external_url'
              label={t('External URL', { defaultValue: 'External URL' })}
              error={errors.external_url}
              touched={touched.external_url}
              required={false}
              placeholder='https://...'
            />
            {status && <div className='alert alert-danger py-3 mb-6'>{status}</div>}
            <div className='d-flex gap-3'>
              <ButtonIndicator
                type='submit'
                label={t('Submit evidence', { defaultValue: 'Submit evidence' })}
                loading={isSubmitting}
                disabled={isSubmitting}
              />
              <button type='button' className='btn btn-light' onClick={onClose}>
                {t('Cancel', { defaultValue: 'Cancel' })}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </HModal>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface SecureTransactionProps {
  transaction: SecureTransactionModel;
  token: string;
  onRefresh: () => void;
  readOnly?: boolean;
}

function SecureTransaction({
  transaction: txn,
  token,
  onRefresh,
  readOnly = false,
}: SecureTransactionProps) {
  const { t } = useTranslation();

  const [showDisputeHistoryWidget, setShowDisputeHistoryWidget] = useState(false);

  const secureStatus = txn.escrow_status;
  const trxAmount = parseFloat(txn.trxamount ?? '0') - parseFloat(txn.buyer_fee ?? '0');

  const transactionReference =
    txn.merchant_reference ?? txn.order ?? txn.external_id ?? txn.fin_trx_id ?? txn.id;
  const description =
    ('description' in txn ? txn.description : undefined) ?? txn.message ?? '—';

  const buyerName = txn.customer?.name ?? txn.payer_name ?? txn.b_party ?? '—';
  const buyerEmail = txn.customer?.email ?? txn.payer_email;
  const buyerPhone = formatPhoneNumber(txn.payer_phone ?? txn.b_party, txn.country);

  const sellerName = txn.account?.name ?? txn.seller_name ?? txn.application?.name ?? '—';
  const sellerReference = txn.seller_reference ?? txn.application?.pk ?? '—';

  const [modal, setModal] = useState<
    'confirm' | 'report' | 'disputeMessage' | 'disputeEvidence' | null
  >(null);
  const close = () => setModal(null);
  const success = () => {
    close();
    onRefresh();
  };

  const canConfirm =
    secureStatus === 'AWAITING_RELEASE' && txn.dispute_status !== 'dispute_opened' && !txn.frozen_by_admin;

  const canReportIssue =
    (secureStatus === 'AWAITING_RELEASE' || secureStatus === 'FUNDED') &&
    txn.dispute_status === 'no_dispute' &&
    !txn.frozen_by_admin;

  const canDisputeMessage = txn.dispute_status === 'dispute_opened';
  const canDisputeEvidence = txn.dispute_status === 'dispute_opened';

  const activeDispute = txn.disputes.find((d) => d.status === 'dispute_opened');

  return (
    <div className='w-100 w-lg-1000px'>
      {/* ── Header ── */}
      <div className='d-flex align-items-center justify-content-between mb-6'>
        <div className='d-flex align-items-center gap-3'>
          <div className='symbol symbol-45px symbol-circle bg-light'>
            <span className='symbol-label'>
              <KTSVG path={'/assets/icons/gen051.svg'} className='svg-icon-2x svg-icon-primary' />
            </span>
          </div>
          <div>
            <h2 className='m-0 fw-bold text-gray-900'>
              {t('Secure Payment', { defaultValue: 'Secure Payment' })}
            </h2>
            <span className='text-muted fs-7'>#{txn.order}</span>
          </div>
        </div>
        <div className='d-flex flex-wrap gap-2 align-items-center'>
          <span className={clsx('badge', getStatusBadgeClass(secureStatus!))}>
            {t(`secure_pay_status.${secureStatus?.toLowerCase()}`)}
          </span>
          {txn.dispute_status !== 'no_dispute' && (
            <span className={clsx('badge', disputeBadge[txn.dispute_status] ?? 'badge-light')}>
              {t('Dispute')}: {t(`dispute_status.${txn.dispute_status}`, { defaultValue: txn.dispute_status })}
            </span>
          )}
          {txn.frozen_by_admin && (
            <span className='badge badge-light-danger'>
              <KTSVG path={'/assets/icons/lock.svg'} className='fs-7 me-1' />
              {t('Frozen', { defaultValue: 'Frozen' })}
            </span>
          )}
        </div>
      </div>

      {/* ── Frozen warning ── */}
      {txn.frozen_by_admin && (
        <div className='alert alert-danger d-flex align-items-start gap-3 mb-5'>
          <KTSVG path={'/assets/icons/information.svg'} className='fs-2 text-danger mt-1' />
          <div>
            <div className='fw-semibold'>{t('Transaction frozen', { defaultValue: 'Transaction frozen' })}</div>
            <div className='fs-7 text-muted'>
              {txn.frozen_reason ?? t('This transaction is currently frozen by admin pending review.', { defaultValue: 'This transaction is currently frozen by admin pending review.' })}
            </div>
          </div>
        </div>
      )}

      {/* ── Escrow trust banner ── */}
      {(secureStatus === 'AWAITING_RELEASE' || secureStatus === 'FUNDED') && (
        <div className='alert alert-primary d-flex align-items-start gap-3 mb-5'>
          <KTSVG path='/assets/icons/shield-tick.svg' className='fs-2 text-primary mt-1' />
          <div>
            <div className='fw-semibold'>
              {t('Funds are held in escrow', { defaultValue: 'Funds are held in escrow' })}
            </div>
            <div className='fs-7 text-muted'>
              {t('Escrow funds description', {
                defaultValue:
                  'Your payment is safely held by MeSomb. Funds will only be released when you confirm the transaction conditions have been fulfilled or if the release rule triggers automatically.',
              })}
            </div>
          </div>
        </div>
      )}

      <div className='row g-6'>
        {/* ── Transaction details ── */}
        <div className='col-12 col-lg-7'>
          {/* ── Active dispute ── */}
          {showDisputeHistoryWidget ? (
            <KTCard className='mt-6 border-warning'>
              <KTCardBody className='py-6 px-6'>
                <div className='d-flex align-items-center justify-content-between mb-4 pe-7'>
                  <h5 className='m-0 text-warning'>{t('Active dispute')}{' - '}{t(`dispute_reason.${activeDispute!.reason}`)}</h5>
                  <span className='badge badge-light-warning'>
                    {t(`dispute_status.${activeDispute!.status}`)}
                  </span>
                  <a style={{position: 'absolute', right: 10, cursor: 'pointer'}} onClick={() => setShowDisputeHistoryWidget(false)}>
                    <KTSVG path={'/assets/icons/cross.svg'} className='fs-6 text-black' />
                  </a>
                </div>
                <div className='separator my-1' />
                <div>
                  <PublicDisputeHistoryWidget
                    token={token}
                    dispute={activeDispute!}
                  />
                </div>
              </KTCardBody>
            </KTCard>
          ) : (
            <div>
              <KTCard>
                <KTCardBody className='py-6 px-6'>
                  <h5 className='mb-4 text-gray-900'>{t('Transaction details', { defaultValue: 'Transaction details' })}</h5>

                  <InfoRow label={t('Reference', { defaultValue: 'Reference' })} value={transactionReference} mono />
                  <InfoRow label={t('Description', { defaultValue: 'Description' })} value={description} />
                  <InfoRow
                    label={t('Amount', { defaultValue: 'Amount' })}
                    value={
                      <div className='text-end'>
                        <div className='fs-4 fw-bold text-gray-900'>
                          {t('intlCurrency', {
                            value: trxAmount,
                            currency: txn.currency,
                          })}
                        </div>
                      </div>
                    }
                  />
                  <InfoRow
                    label={t('Secure Pay Fee')}
                    value={
                      <div className='text-end'>
                        <div className='fs-4 fw-bold text-gray-900'>
                          {t('intlCurrency', {
                            value: txn.fee_amount,
                            currency: txn.currency,
                          })}
                        </div>
                        <div className='text-muted fs-8'>
                          {t('Paid by', { defaultValue: 'Paid by' })}:{' '}{t(`secure_pay.fee_strategy.${txn.fee_strategy.toLowerCase()}`)}
                        </div>
                      </div>
                    }
                  />
                  {txn.trxamount && (
                    <InfoRow
                      label={t('Charged amount', { defaultValue: 'Charged amount' })}
                      value={t('intlCurrency', {
                        value: txn.trxamount,
                        currency: txn.currency,
                      })}
                    />
                  )}
                  <InfoRow
                    label={t('Release rule', { defaultValue: 'Release rule' })}
                    value={t(`release_rule.${txn.release_rule}`)}
                  />
                  <InfoRow
                    label={t('Operator ID')}
                    value={txn.fin_trx_id}
                  />
                  {txn.escrow_status && (
                    <InfoRow
                      label={t('Escrow status', { defaultValue: 'Escrow status' })}
                      value={t(`secure_pay_status.${txn.escrow_status.toLowerCase()}`, {
                        defaultValue: txn.escrow_status.replace(/_/g, ' '),
                      })}
                    />
                  )}
                  {txn.fulfillment_status && (
                    <InfoRow
                      label={t('Fulfillment status', { defaultValue: 'Fulfillment status' })}
                      value={txn.fulfillment_status.replace(/_/g, ' ')}
                    />
                  )}
                  {txn.expires_at && (
                    <InfoRow label={t('Expires at', { defaultValue: 'Expires at' })} value={formatDateTime(txn.expires_at)} />
                  )}
                  {txn.funded_at && <InfoRow label={t('Funded at', { defaultValue: 'Funded at' })} value={formatDateTime(txn.funded_at)} />}
                  {txn.awaiting_release_at && (
                    <InfoRow
                      label={t('Awaiting release at', { defaultValue: 'Awaiting release at' })}
                      value={formatDateTime(txn.awaiting_release_at)}
                    />
                  )}
                  {txn.released_at && (
                    <InfoRow label={t('Released at', { defaultValue: 'Released at' })} value={formatDateTime(txn.released_at)} />
                  )}
                  {txn.refunded_at && (
                    <InfoRow label={t('Refunded at', { defaultValue: 'Refunded at' })} value={formatDateTime(txn.refunded_at)} />
                  )}
                  <InfoRow label={t('Created at', { defaultValue: 'Created at' })} value={formatDateTime(txn.created_at)} />
                </KTCardBody>
              </KTCard>

              {/*── Parties ──*/}
              <KTCard className='mt-6'>
                <KTCardBody className='py-6 px-6'>
                  <h5 className='mb-4 text-gray-900'>{t('Parties', { defaultValue: 'Parties' })}</h5>
                  <div className='row g-4'>
                    <div className='col-6'>
                      <div className='p-3 border rounded bg-light-primary'>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <KTSVG path='/assets/icons/profile-circle.svg' className='fs-5 text-primary' />
                          <span className='fw-semibold fs-7 text-gray-900'>{t('Buyer', { defaultValue: 'Buyer' })}</span>
                        </div>
                        <div className='fw-semibold text-gray-900'>{buyerName}</div>
                        {buyerEmail && <div className='text-muted fs-8'>{buyerEmail}</div>}
                        {buyerPhone && <div className='text-muted fs-8'>{buyerPhone}</div>}
                      </div>
                    </div>
                    <div className='col-6'>
                      <div className='p-3 border rounded bg-light-success'>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <KTSVG path='/assets/icons/shop.svg' className='fs-5 text-success' />
                          <span className='fw-semibold fs-7 text-gray-900'>{t('Seller', { defaultValue: 'Seller' })}</span>
                        </div>
                        <div className='fw-semibold text-gray-900'>{sellerName}</div>
                        <div className='text-muted fs-8'>{sellerReference}</div>
                      </div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
            </div>
          )}
        </div>

        {/* ── Actions sidebar ── */}
        <div className='col-12 col-lg-5'>
          <div className='position-sticky' style={{ top: 20 }}>
            {!readOnly && (
              <KTCard className='mb-6'>
                <KTCardBody className='py-6 px-6'>
                  <h5 className='mb-4 text-gray-900'>{t('Actions', { defaultValue: 'Actions' })}</h5>

                  {canConfirm && (
                    <div className='mb-3'>
                      <button
                        type='button'
                        className='btn btn-success w-100 d-flex align-items-center justify-content-center gap-2'
                        onClick={() => setModal('confirm')}
                      >
                        <KTSVG path='/assets/icons/shield-tick.svg' className='fs-4' />
                        {t('Confirm transaction', { defaultValue: 'Confirm transaction' })}
                      </button>
                      <div className='text-muted fs-8 mt-2'>
                        {t('Confirm transaction help', {
                          defaultValue:
                            'Releases funds to the seller once you have verified your order has been fulfilled.',
                        })}
                      </div>
                    </div>
                  )}

                  {canReportIssue && (
                    <div className='mb-3'>
                      <button
                        type='button'
                        className='btn btn-danger w-100 d-flex align-items-center justify-content-center gap-2'
                        onClick={() => setModal('report')}
                      >
                        <KTSVG path='/assets/icons/information.svg' className='fs-4' />
                        {t('Report an issue', { defaultValue: 'Report an issue' })}
                      </button>
                      <div className='text-muted fs-8 mt-2'>
                        {t('Report issue help', {
                          defaultValue:
                            'Opens a dispute if you have not received your item or if something is wrong.',
                        })}
                      </div>
                    </div>
                  )}

                  {(canDisputeMessage || canDisputeEvidence) && (
                    <div className='mb-3'>
                      <button
                        type='button'
                        className='btn btn-warning w-100 d-flex align-items-center justify-content-center gap-2'
                        onClick={() => setShowDisputeHistoryWidget(true)}
                      >
                        <KTSVG path='/assets/icons/chat.svg' className='fs-4' />
                        {t('Open dispute history')}
                      </button>
                    </div>
                  )}

                  {!canConfirm && !canReportIssue && !canDisputeMessage && !canDisputeEvidence && (
                    <div className='text-muted fs-7'>
                      {t('No actions available', { defaultValue: 'No actions available for this transaction.' })}
                    </div>
                  )}
                </KTCardBody>
              </KTCard>
            )}

            {/* ── Audit log ── */}
            {txn.audit_logs.length > 0 && (
              <KTCard className='mt-6'>
                <KTCardBody className='py-6 px-6'>
                  <h5 className='mb-4 text-gray-900'>{t('History', { defaultValue: 'History' })}</h5>
                  {txn.audit_logs.map((log, i) => (
                    <div key={i} className='d-flex align-items-start gap-3 mb-4'>
                      <div
                        className='rounded-circle bg-light-primary d-flex align-items-center justify-content-center flex-shrink-0'
                        style={{width: 28, height: 28}}
                      >
                        <KTSVG path={'/assets/icons/clock.svg'} className={'text-primary'} />
                      </div>
                      <div>
                        <div className='d-flex align-items-center gap-2'>
                          <span className='fw-semibold text-gray-800 fs-7'>
                            {t(`secure_pay_action.${log.action}`, {
                              defaultValue: log.action.replace(/_/g, ' '),
                            })}
                          </span>
                          <span className='badge badge-light fs-9'>{log.actor_type}</span>
                        </div>
                        {log.reason && <div className='text-muted fs-8'>{log.reason}</div>}
                        <div className='text-muted fs-9'>{formatDateTime(log.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </KTCardBody>
              </KTCard>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className='d-flex align-items-center justify-content-center mt-6 text-muted fs-8'>
        <img src={'/logo.png'} width={16} className='h-12px me-1 opacity-75'
             alt='MeSomb'/>
        {t('Powered by')} <span className='fw-semibold ms-1'>MeSomb</span>
      </div>

      {!readOnly && (
        <>
          <ConfirmModal
            show={modal === 'confirm'}
            token={token}
            onClose={close}
            onSuccess={success}
          />
          <ReportIssueModal
            show={modal === 'report'}
            token={token}
            onClose={close}
            onSuccess={success}
          />
          <DisputeMessageModal
            show={modal === 'disputeMessage'}
            token={token}
            onClose={close}
            onSuccess={success}
          />
          <DisputeEvidenceModal
            show={modal === 'disputeEvidence'}
            token={token}
            onClose={close}
            onSuccess={success}
          />
        </>
      )}
    </div>
  );
}

export default SecureTransaction;

